import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@ncp/api-client";
import { useTranslation } from "@ncp/i18n";
import { SaveError, useSave } from "@ncp/ui";

export type ChangeSetStatus = "Draft" | "PendingApproval" | "Approved" | "Scheduled" | "Published" | "Rejected" | "RolledBack" | "Failed" | "Cancelled";
export type ChangeItemKind = "Publish" | "Retire" | "Setting" | "Flag" | "Role";

export interface ChangeSetItem {
  id: string;
  kind: ChangeItemKind;
  definitionType: string;
  definitionKey: string;
  definitionId?: string | null;
  targetVersion?: number | null;
  previousVersion?: number | null;
  title: string;
  diffSummary?: string | null;
  diffJson?: string | null;
  newValueJson?: string | null;
  oldValueJson?: string | null;
  runningCases?: number | null;
  sortOrder: number;
  error?: string | null;
}

export interface ChangeSet {
  id: string;
  title: string;
  description?: string | null;
  status: ChangeSetStatus;
  requestedByUserId?: string | null;
  requestedByName: string;
  createdAt: string;
  updatedAt: string;
  submittedAt?: string | null;
  approvedByUserId?: string | null;
  approvedByName?: string | null;
  approvedAt?: string | null;
  approvalNote?: string | null;
  rejectionReason?: string | null;
  effectiveFrom?: string | null;
  publishedAt?: string | null;
  publishedByName?: string | null;
  rolledBackAt?: string | null;
  rolledBackByName?: string | null;
  rollbackReason?: string | null;
  rollbackOfChangeSetId?: string | null;
  advisorReviewMarkdown?: string | null;
  advisorRisk?: string | null;
  advisorReviewedAt?: string | null;
  sourcePackageId?: string | null;
  error?: string | null;
  selfApproved: boolean;
  itemCount: number;
  items: ChangeSetItem[];
  canApprove: boolean;
}

export interface ChangeSetEvaluation {
  itemId: string; agentKey: string; version?: number | null; run: boolean; succeeded: boolean;
  passed: number; total: number; redTeamPassed: number; redTeamTotal: number; runByName?: string | null; runAt?: string | null;
}

export interface ChangeSetDetail {
  changeSet: ChangeSet;
  advisories: Array<{ id: string; title: string; summary: string; confidence: number; status: string; createdAt: string; contentMarkdown: string }>;
  rollbacks: Array<{ id: string; title: string; status: string; publishedAt?: string | null }>;
  /** The go-live evaluation of every configurable agent version in the release; approval is blocked until each one passed. */
  evaluations?: ChangeSetEvaluation[];
  /** Condition 3.7: the items that change a model or an agent's instructions, and whether approval here needs the Department's written approval. */
  aiUpdate?: { items: string[]; departmentApprovalRequired: boolean } | null;
  /** Condition 1.5: whether approving this release needs the Department's written approval, and an urgent security fix's run still due. */
  gate?: { departmentApprovalRequired: boolean; referenceRunDue?: string | null; referenceRunOverdue: boolean } | null;
}

/**
 * An approval; where the Department's approval is required (every release on a production installation, a model or an agent's instructions)
 * its reference and the reference-file run, or for an urgent security fix the approval alone (condition 1.5).
 */
export interface ApproveInput { reason?: string | null; departmentApproval?: string | null; referenceRun?: string | null; urgentSecurityFix?: boolean }

export interface ReleaseSummary {
  counts: Partial<Record<ChangeSetStatus, number>>;
  nextScheduled?: { id: string; title: string; effectiveFrom: string } | null;
  lastPublished?: { id: string; title: string; publishedAt: string; publishedByName?: string | null } | null;
  /** Urgent security fixes whose run of the full set is still to be recorded (condition 1.5). */
  urgentRuns?: Array<{ id: string; title: string; dueOn: string; overdue: boolean }>;
  /** The Department's approval of the version this installation runs, required to start on a production installation. */
  installation?: { productionGate: boolean; version: string; approvedVersion?: string | null; departmentApproval?: string | null; referenceRun?: string | null; urgentSecurityFix: boolean; approvedOn?: string | null; referenceRunDue?: string | null } | null;
}

export interface ChangeItemInput {
  definitionType: string;
  definitionKey: string;
  definitionId?: string | null;
  targetVersion?: number | null;
  newValueJson?: string | null;
  kind?: ChangeItemKind;
}

export interface JsonPatchOp { op: "add" | "remove" | "replace"; path: string; value?: unknown }

export const OPEN_STATUSES: ChangeSetStatus[] = ["Draft", "PendingApproval", "Approved", "Scheduled"];
export const CLOSED_STATUSES: ChangeSetStatus[] = ["Published", "Rejected", "RolledBack", "Failed", "Cancelled"];

export function useChangeSets(filters: { status?: ChangeSetStatus[]; q?: string; take?: number }) {
  const api = useApi();
  const params = new URLSearchParams();
  if (filters.status?.length) params.set("status", filters.status.join(","));
  if (filters.q) params.set("q", filters.q);
  if (filters.take) params.set("take", String(filters.take));
  return useQuery({ queryKey: ["change-sets", params.toString()], queryFn: () => api.get<ChangeSet[]>(`/api/platform/change-sets?${params}`), refetchInterval: 30_000 });
}

export function useReleaseSummary() {
  const api = useApi();
  return useQuery({ queryKey: ["change-sets", "summary"], queryFn: () => api.get<ReleaseSummary>("/api/platform/change-sets/summary"), refetchInterval: 30_000 });
}

export function useChangeSet(id: string | null) {
  const api = useApi();
  return useQuery({ queryKey: ["change-set", id], queryFn: () => api.get<ChangeSetDetail>(`/api/platform/change-sets/${id}`), enabled: !!id, refetchInterval: 15_000 });
}

export function useCurrentDraft() {
  const api = useApi();
  return useQuery({ queryKey: ["change-sets", "current"], queryFn: async () => (await api.get<ChangeSet | null | "">("/api/platform/change-sets/current")) || null });
}

/**
 * A publish, approval or rollback that ran and failed answers with the change set marked Failed (its error kept on it): that is a failure with
 * its cause, never "Published." (S 2.8).
 */
export function releaseOutcome(cs: ChangeSet): ChangeSet {
  if (cs?.status === "Failed") throw new SaveError("server", cs.error ?? null, 200, cs);
  return cs;
}

/** Mutations for one change set; every success invalidates the release queries (and a failed publish, which is stored as Failed). */
export function useReleaseActions(id?: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  const { t } = useTranslation();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["change-sets"] });
    void qc.invalidateQueries({ queryKey: ["change-set"] });
    void qc.invalidateQueries({ queryKey: ["workflow-definitions"] });
    void qc.invalidateQueries({ queryKey: ["platform-overview"] });
  };
  const post = <T,>(path: string, body?: unknown) => api.post<T>(`/api/platform/change-sets${path}`, body ?? {});
  return {
    create: useSave({ mutationFn: (input: { title: string; description?: string | null; effectiveFrom?: string | null }) => post<ChangeSet>("", input), onSuccess: invalidate }),
    update: useSave({ mutationFn: (input: { title: string; description?: string | null; effectiveFrom?: string | null }) => api.put<ChangeSet>(`/api/platform/change-sets/${id}`, input), onSuccess: invalidate }),
    addItem: useSave({ mutationFn: (item: ChangeItemInput) => post<ChangeSet>(`/${id}/items`, item), onSuccess: invalidate }),
    removeItem: useSave({ mutationFn: (itemId: string) => api.delete<ChangeSet>(`/api/platform/change-sets/${id}/items/${itemId}`), onSuccess: invalidate }),
    stage: useSave({ mutationFn: (input: { item: ChangeItemInput; titleHint?: string | null }) => post<ChangeSet>("/stage", input), onSuccess: invalidate }),
    submit: useSave({ mutationFn: () => post<ChangeSet>(`/${id}/submit`), onSuccess: invalidate }),
    approve: useSave({ mutationFn: async (input?: string | null | ApproveInput) => releaseOutcome(await post<ChangeSet>(`/${id}/approve`, typeof input === "object" && input !== null ? { reason: input.reason ?? null, departmentApproval: input.departmentApproval ?? null, referenceRun: input.referenceRun ?? null, urgentSecurityFix: input.urgentSecurityFix === true } : { reason: input ?? null })), failed: t("admin:releases.failed.approve"), onSuccess: invalidate, onError: () => invalidate() }),
    reject: useSave({ mutationFn: (reason: string) => post<ChangeSet>(`/${id}/reject`, { reason }), onSuccess: invalidate }),
    publish: useSave({ mutationFn: async () => releaseOutcome(await post<ChangeSet>(`/${id}/publish`)), failed: t("admin:releases.failed.publish"), onSuccess: invalidate, onError: () => invalidate() }),
    cancel: useSave({ mutationFn: () => post<ChangeSet>(`/${id}/cancel`), onSuccess: invalidate }),
    reopen: useSave({ mutationFn: () => post<ChangeSet>(`/${id}/reopen`), onSuccess: invalidate }),
    rollback: useSave({ mutationFn: async (reason: string) => releaseOutcome(await post<ChangeSet>(`/${id}/rollback`, { reason })), failed: t("admin:releases.failed.rollback"), onSuccess: invalidate, onError: () => invalidate() }),
    review: useSave({ mutationFn: () => post<{ risk: string; markdown: string; findings: string[] }>(`/${id}/review`), onSuccess: invalidate }),
  };
}

/** Parses the JSON Patch stored on an item; tolerant of nulls and malformed payloads. */
export function parsePatch(json?: string | null): JsonPatchOp[] {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json) as unknown;
    return Array.isArray(parsed) ? (parsed as JsonPatchOp[]) : [];
  } catch {
    return [];
  }
}

export function prettyJson(json?: string | null): string {
  if (!json) return "";
  try {
    return JSON.stringify(JSON.parse(json), null, 2);
  } catch {
    return json;
  }
}
